import { i18n } from "@lingui/core";
import { I18nProvider, useLingui } from "@lingui/react";
import { darkTheme, RainbowKitProvider, type Theme, type Locale } from "@rainbow-me/rainbowkit";
import "@rainbow-me/rainbowkit/styles.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import merge from "lodash/merge";
import React, { useMemo } from "react";
import { WagmiProvider } from "wagmi";

import { AuthProvider } from "context/AuthContext/AuthProvider";
import { GlobalStateProvider } from "context/GlobalContext/GlobalContextProvider";
import { BridgeProvider } from "lib/bridge/bridge";
import { getRainbowKitConfig } from "lib/wallets/rainbowKitConfig";

import ConnectWalletModal from "components/ConnectWalletModal/ConnectWalletModal";
import { SignInModal } from "components/SignInModal/SignInModal";

const walletTheme = merge(darkTheme(), {
  colors: {
    modalBackground: "#161616",
    accentColor: "#9da5f2",
    menuItemBackground: "#808aff14",
  },
  radii: {
    modal: "4px",
    menuButton: "4px",
  },
} as Theme);

const queryClient = new QueryClient();

const appLocale2RainbowLocaleMap: Record<string, Locale> = {
  de: "en",
  en: "en",
  es: "es",
  fr: "fr",
  ja: "ja",
  ko: "ko",
  ru: "ru",
  zh: "zh",
};

function RainbowKitLocalProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useLingui();
  const locale = useMemo(() => appLocale2RainbowLocaleMap[i18n.locale] ?? "en", [i18n.locale]);

  return (
    <RainbowKitProvider theme={walletTheme} locale={locale} modalSize="compact">
      {children}
    </RainbowKitProvider>
  );
}

export function AppProviders({ children }: { children: React.ReactNode }) {
  return (
    <WagmiProvider config={getRainbowKitConfig()}>
      <QueryClientProvider client={queryClient}>
        <I18nProvider i18n={i18n}>
          <GlobalStateProvider>
            <RainbowKitLocalProvider>
              <AuthProvider>
                <BridgeProvider>{children}</BridgeProvider>
                <ConnectWalletModal />
                <SignInModal />
              </AuthProvider>
            </RainbowKitLocalProvider>
          </GlobalStateProvider>
        </I18nProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}
